/**
 * Contact Service
 * 문의 관리 비즈니스 로직
 */

import { ContactRepository } from '../repositories';
import {
  Contact,
  ContactStatus,
  isContactPending,
  isContactInProgress,
  isContactCompleted,
  ServiceType,
} from '../models';

// Aliases for compatibility
const isPending = isContactPending;
const isInProgress = isContactInProgress;
const isCompleted = isContactCompleted;

export interface AssignContactRequest {
  contactId: string;
  assignToId: string;
}

export interface RespondToContactRequest {
  contactId: string;
  response: string;
}

export interface RejectContactRequest {
  contactId: string;
  reason: string;
}

export class ContactService {
  constructor(private repository: ContactRepository) {}

  /**
   * 모든 문의 조회
   */
  async getAllContacts(serviceType?: ServiceType): Promise<Contact[]> {
    const contacts = await this.repository.getAllContacts(serviceType);

    // 최신 문의 순으로 정렬
    return contacts.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  /**
   * 대기 중인 문의 조회
   */
  async getPendingContacts(serviceType?: ServiceType): Promise<Contact[]> {
    const contacts = await this.repository.getPendingContacts(serviceType);

    // 방어 코드: 배열이 아니면 빈 배열 반환
    if (!Array.isArray(contacts)) {
      console.error('❌ getPendingContacts: 배열이 아닌 데이터 반환됨:', contacts);
      return [];
    }

    // 오래된 문의 순으로 정렬 (먼저 처리해야 할 것부터)
    return contacts.sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }

  /**
   * 처리 중인 문의 조회
   */
  async getInProgressContacts(serviceType?: ServiceType): Promise<Contact[]> {
    const contacts = await this.repository.getInProgressContacts(serviceType);

    // 방어 코드: 배열이 아니면 빈 배열 반환
    if (!Array.isArray(contacts)) {
      console.error('❌ getInProgressContacts: 배열이 아닌 데이터 반환됨:', contacts);
      return [];
    }

    return contacts;
  }

  /**
   * 내가 맡은 문의 조회
   */
  async getMyContacts(): Promise<Contact[]> {
    return await this.repository.getMyContacts();
  }

  /**
   * 문의 상세 조회
   */
  async getContact(id: string): Promise<Contact> {
    return await this.repository.getContactById(id);
  }

  /**
   * 문의 배정
   */
  async assignContact(request: AssignContactRequest): Promise<Contact> {
    if (!request.contactId || !request.assignToId) {
      throw new Error('문의 ID와 담당자 ID가 필요합니다.');
    }

    // 현재 상태 확인
    const contact = await this.repository.getContactById(request.contactId);

    if (isCompleted(contact)) {
      throw new Error('이미 완료된 문의는 재배정할 수 없습니다.');
    }

    return await this.repository.assignContact({
      contact_id: request.contactId,
      admin_id: request.assignToId,
    });
  }

  /**
   * 문의 응답
   */
  async respondToContact(request: RespondToContactRequest): Promise<Contact> {
    if (!request.contactId || !request.response) {
      throw new Error('문의 ID와 응답 내용이 필요합니다.');
    }

    if (request.response.trim().length < 10) {
      throw new Error('응답은 10자 이상 입력해주세요.');
    }

    // 현재 상태 확인
    const contact = await this.repository.getContactById(request.contactId);

    if (isPending(contact)) {
      throw new Error('배정되지 않은 문의입니다. 먼저 담당자를 배정해주세요.');
    }

    if (isCompleted(contact)) {
      throw new Error('이미 완료된 문의입니다.');
    }

    return await this.repository.respondToContact({
      contact_id: request.contactId,
      response: request.response,
    });
  }

  /**
   * 문의 거부
   */
  async rejectContact(request: RejectContactRequest): Promise<Contact> {
    if (!request.contactId || !request.reason) {
      throw new Error('문의 ID와 거부 사유가 필요합니다.');
    }

    if (request.reason.trim().length < 10) {
      throw new Error('거부 사유는 10자 이상 입력해주세요.');
    }

    return await this.repository.rejectContact({
      contact_id: request.contactId,
      rejection_reason: request.reason,
    });
  }

  /**
   * 문의 상태 변경
   */
  async updateContactStatus(
    contactId: string,
    status: ContactStatus
  ): Promise<Contact> {
    if (!contactId || !status) {
      throw new Error('문의 ID와 상태가 필요합니다.');
    }

    return await this.repository.updateContactStatus({
      contact_id: contactId,
      status,
    });
  }

  /**
   * 대기 중인 문의 건수 조회
   */
  async getPendingCount(serviceType?: ServiceType): Promise<number> {
    const pending = await this.getPendingContacts(serviceType);
    return pending.length;
  }

  /**
   * 문의 상태별 통계
   */
  async getContactStats(serviceType?: ServiceType): Promise<{
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    rejected: number;
  }> {
    const all = await this.getAllContacts(serviceType);

    return {
      total: all.length,
      pending: all.filter((c) => isPending(c)).length,
      inProgress: all.filter((c) => isInProgress(c)).length,
      completed: all.filter((c) => isCompleted(c)).length,
      rejected: all.filter((c) => c.status === 'rejected').length,
    };
  }

  /**
   * 문의 검색
   */
  searchContacts(contacts: Contact[], query: string): Contact[] {
    if (!query || query.trim().length === 0) {
      return contacts;
    }

    const lowerQuery = query.toLowerCase();

    return contacts.filter(
      (contact) =>
        contact.name.toLowerCase().includes(lowerQuery) ||
        contact.email.toLowerCase().includes(lowerQuery) ||
        contact.phone?.includes(query) ||
        contact.message.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * 응답 시간 계산 (분 단위)
   */
  calculateResponseTime(contact: Contact): number | null {
    if (!contact.responded_at) return null;

    const created = new Date(contact.created_at).getTime();
    const responded = new Date(contact.responded_at).getTime();

    return Math.floor((responded - created) / (1000 * 60));
  }

  /**
   * 평균 응답 시간 계산
   */
  calculateAverageResponseTime(contacts: Contact[]): number {
    const completedContacts = contacts.filter((c) => isCompleted(c));

    if (completedContacts.length === 0) return 0;

    const totalMinutes = completedContacts.reduce((sum, contact) => {
      const responseTime = this.calculateResponseTime(contact);
      return sum + (responseTime || 0);
    }, 0);

    return Math.floor(totalMinutes / completedContacts.length);
  }
}
